import { useState, useEffect } from 'react';

const STORAGE_KEY = 'user_residency';
const GEOLOCATION_CACHE_KEY = 'geolocation_cache';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

interface GeolocationData {
  country_code: string;
  timestamp: number;
}

/**
 * Hook to detect user's country via IP geolocation
 * Includes localStorage persistence to avoid repeated API calls
 */
export const useGeolocation = () => {
  const [country, setCountry] = useState<string>('US'); // Default to US
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const detectCountry = async () => {
      try {
        // 1. Check if user has manually set a preference
        const savedResidency = localStorage.getItem(STORAGE_KEY);
        if (savedResidency) {
          setCountry(savedResidency);
          setIsLoading(false);
          return;
        }

        // 2. Check for cached geolocation data
        const cachedData = localStorage.getItem(GEOLOCATION_CACHE_KEY);
        if (cachedData) {
          const parsed: GeolocationData = JSON.parse(cachedData);
          const isExpired = Date.now() - parsed.timestamp > CACHE_DURATION;
          
          if (!isExpired && parsed.country_code) {
            setCountry(parsed.country_code);
            setIsLoading(false);
            return;
          }
        }

        // 3. Fetch from IP geolocation API
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
        
        if (!response.ok) throw new Error('Geolocation API failed');
        
        const data = await response.json();
        
        if (data.country_code) {
          const countryCode = data.country_code.toUpperCase();
          
          // Cache the result
          const cacheData: GeolocationData = {
            country_code: countryCode,
            timestamp: Date.now()
          };
          localStorage.setItem(GEOLOCATION_CACHE_KEY, JSON.stringify(cacheData));
          
          setCountry(countryCode);
        }
      } catch (error) {
        console.error('Failed to detect country:', error);
        // Keep default US on error
      } finally {
        setIsLoading(false);
      }
    };

    detectCountry();
  }, []);

  const saveResidency = (countryCode: string) => {
    localStorage.setItem(STORAGE_KEY, countryCode);
    setCountry(countryCode);
  };

  return { country, isLoading, saveResidency };
};
